import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { SignIn } from "./auth/SignIn";
import { SetPasswordPanel } from "./auth/SetPasswordPanel";
import { initialAuthCallback, requiresPasswordSetup } from "./auth/auth-callback";
import { JoinAccountPanel } from "./join/JoinAccountPanel";
import { CaptureScreen } from "./capture/CaptureScreen";
import { DutyProgress } from "./duty/DutyProgress";
import { markCaptured } from "./duty/duty-progress";
import { deriveOfficerName } from "./duty/officer-name";
import { getOrCreateDeviceId } from "./capture/device-id";
import { useGuardCheckpoints } from "./capture/route-checkpoints";
import { createAnalytics } from "./lib/analytics";
import { describeDevice, detectDeviceContext } from "./lib/device";
import { useFeatureFlags, type FlagsRpcClient } from "./flags/use-feature-flags";
import { WebAccessBlocked } from "./flags/WebAccessBlocked";
import { WebAccessUnverified } from "./flags/WebAccessUnverified";
import { readRuntimeConfig, type LiveConfig } from "./lib/live-config";
import { createErrorReporter, type ErrorReporter } from "./lib/sentry";
import { createSupabaseClient } from "./lib/supabase-client";
import { MyShiftsPanel } from "./schedule/MyShiftsPanel";
import { OpenShiftsPanel } from "./schedule/OpenShiftsPanel";
import type { MyShiftsQueryClient } from "./schedule/my-shifts";
import type { ClaimOpenShiftRpcClient, OpenShiftsQueryClient } from "./schedule/open-shifts";
import type { ScheduleResponseRpcClient } from "./schedule/submit-response";
import { BuildIdentity } from "./status/BuildIdentity";
import { FieldStatus } from "./status/FieldStatus";
import { TimeclockPanel } from "./timeclock/TimeclockPanel";
import type { TimePunchRpcClient } from "./timeclock/submit-punch";
import type { MyAccount } from "./data/accounts.ts";
import { formatAccountRoleLabel } from "../../../packages/shared/src/account-context.ts";
import {
  createLoadingGuardWebAccountGate,
  loadGuardWebAccountGate,
  selectGuardWebAccount,
  type GuardWebAccountGate,
} from "./workspace/account-gate.ts";
import { WorkspaceGateScreen } from "./workspace/WorkspaceGateScreen.tsx";
import { BrandMark } from "./components/BrandMark";
import { FieldInbox } from "./messages/FieldInbox.tsx";

// Top-level composition for Guard Web:
//   1. Read + validate the host-injected runtime config (secret-refusing gate). A missing/invalid
//      config renders an honest BootScreen with a SAFE reason (never echoes a key value).
//   2. Build the supabase client once and wrap the app in AuthProvider (stay-logged-in).
//   3. Gate on auth status → restoring session / SignIn / the guard shell.
// The capture shell (QR + GPS) replaces GuardShell's placeholder body in the next slice (#25).

export function App() {
  const result = useMemo(() => readRuntimeConfig(), []);
  if (!result.ok) {
    return (
      <BootScreen
        tone="error"
        headline="Configuration required"
        detail="This deployment is missing required public configuration. Fix the items below and reload - no values are ever shown here."
        problems={result.problems ?? [result.reason]}
      />
    );
  }
  return <ConfiguredApp config={result.config} />;
}

function ConfiguredApp({ config }: { config: LiveConfig }) {
  const client = useMemo(() => createSupabaseClient(config), [config]);
  const deviceId = useMemo(() => getOrCreateDeviceId(), []);
  // Crash/error reporting — frozen no-op without a runtime Sentry DSN (replay/traces off).
  const errorReporter = useMemo(
    () =>
      createErrorReporter({
        dsn: config.sentryDsn ?? undefined,
        environment: config.sentryEnvironment ?? undefined,
        surface: "guard-web",
        release: __APP_VERSION__,
      }),
    [config.sentryDsn, config.sentryEnvironment],
  );
  return (
    <AuthProvider client={client}>
      <AuthGate client={client} config={config} deviceId={deviceId} errorReporter={errorReporter} />
    </AuthProvider>
  );
}

function AuthGate({
  client,
  config,
  deviceId,
  errorReporter,
}: {
  client: SupabaseClient;
  config: LiveConfig;
  deviceId: string;
  errorReporter: ErrorReporter;
}) {
  const { status, user, signOut } = useAuth();
  // Why the officer arrived, snapshotted before supabase-js consumed the callback fragment.
  const callback = useMemo(() => initialAuthCallback(), []);
  const [passwordSet, setPasswordSet] = useState(false);

  if (status === "loading") {
    return <BootScreen tone="muted" headline="Guard Web" detail="Restoring your session…" />;
  }
  if (status === "unauthenticated") {
    // An expired or already-used invite link leaves no session; say so instead of showing a bare
    // password form to someone who has never had a password.
    return <SignIn notice={callback.kind === "error" ? callback.reason : null} />;
  }
  // Invite/recovery links sign the officer in WITHOUT a password. Setting one is the only way
  // back in later, on this surface or the native app, so it is not skippable.
  if (!passwordSet && requiresPasswordSetup(callback)) {
    return (
      <SetPasswordPanel
        client={client}
        email={user?.email ?? null}
        onComplete={() => setPasswordSet(true)}
        onSignOut={() => void signOut()}
      />
    );
  }
  return (
    <WorkspaceShell client={client} config={config} deviceId={deviceId} errorReporter={errorReporter} />
  );
}

function WorkspaceShell({
  client,
  config,
  deviceId,
  errorReporter,
}: {
  client: SupabaseClient;
  config: LiveConfig;
  deviceId: string;
  errorReporter: ErrorReporter;
}) {
  const { user, signOut } = useAuth();
  const [gate, setGate] = useState<GuardWebAccountGate>(() => createLoadingGuardWebAccountGate());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectingAccountId, setSelectingAccountId] = useState<string | null>(null);

  const refreshGate = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const next = await loadGuardWebAccountGate(client, config.accountId);
      setGate(next);
    } finally {
      setIsRefreshing(false);
    }
  }, [client, config.accountId]);

  useEffect(() => {
    void refreshGate();
  }, [refreshGate]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshGate();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshGate]);

  const effectiveConfig = useMemo<LiveConfig | null>(() => {
    if (gate.status !== "ready") {
      return null;
    }
    return {
      ...config,
      accountId: gate.activeAccountId,
      accountName: gate.activeAccount.accountName,
    };
  }, [config, gate]);

  // No membership is the normal state for an invitee, not a failure: resolve pending invitations
  // (product lock 1.4/F2) before falling back to "No workspace access".
  if (gate.status === "blocked") {
    return (
      <div className="boot">
        <main className="boot__card" role="main">
          <JoinAccountPanel
            client={client}
            signedInEmail={user?.email ?? null}
            onJoined={() => void refreshGate()}
            onSignOut={() => void signOut()}
          />
        </main>
      </div>
    );
  }

  if (gate.status !== "ready" || effectiveConfig === null) {
    return (
      <div className="boot">
        <main className="boot__card" role="main">
          <WorkspaceGateScreen
            gate={gate}
            isRefreshing={isRefreshing}
            onRetry={() => void refreshGate()}
            onSelectAccount={(accountId) => {
              setSelectingAccountId(accountId);
              try {
                setGate(selectGuardWebAccount(accountId, gate.accounts, config.accountId));
              } finally {
                setSelectingAccountId(null);
              }
            }}
            onSignOut={() => void signOut()}
            selectingAccountId={selectingAccountId}
          />
        </main>
      </div>
    );
  }

  return (
    <GuardShell
      client={client}
      config={effectiveConfig}
      deviceId={deviceId}
      accounts={gate.accounts}
      errorReporter={errorReporter}
      onSelectWorkspace={(accountId) => {
        setGate(selectGuardWebAccount(accountId, gate.accounts, config.accountId));
      }}
    />
  );
}

function GuardShell({
  client,
  config,
  deviceId,
  accounts,
  errorReporter,
  onSelectWorkspace,
}: {
  client: SupabaseClient;
  config: LiveConfig;
  deviceId: string;
  accounts: readonly MyAccount[];
  errorReporter: ErrorReporter;
  onSelectWorkspace: (accountId: string) => void;
}) {
  const { user, signOut } = useAuth();
  const device = detectDeviceContext();
  const routeCheckpoints = useGuardCheckpoints(client, config.accountId, user?.id ?? "");
  // Everything on the web surface sits behind a tenant/user flag. The hosted resolver is live, so
  // an UNVERIFIED verdict fails closed: capture never renders from defaults — only from a scoped
  // server/cached verdict. The client gate steers; sync_scans enforces server-side via appSurface.
  const userId = user?.id ?? "";
  const { flags, source, loading } = useFeatureFlags(
    client as unknown as FlagsRpcClient,
    config.accountId,
    userId,
  );
  const verdictKnown = source !== "default";
  const blocked = verdictKnown && !flags.webAccess;
  const [view, setView] = useState<"duty" | "inbox" | "status">("duty");
  // Duty-home session memory (ship-dark behind web_duty_home) — which checkpoints this session
  // has captured; honest scope: resets on reload, console keeps the record.
  const [capturedIds, setCapturedIds] = useState<ReadonlySet<string>>(new Set());
  // Allowlist-only analytics — policy-gated like everything else: arms only once a verdict allows it.
  const analytics = useMemo(
    () =>
      createAnalytics({
        key: config.posthogKey,
        host: config.posthogHost,
        enabled: verdictKnown && flags.analytics,
        surface: "guard-web",
      }),
    [config.posthogKey, config.posthogHost, verdictKnown, flags.analytics],
  );
  useEffect(() => {
    if (!verdictKnown) {
      return;
    }
    if (userId !== "") {
      const accountTail = config.accountId.slice(-4);
      analytics.identify(userId, accountTail);
      errorReporter.identify(userId, accountTail);
    }
    analytics.capture("app_booted", {
      appVersion: __APP_VERSION__,
      formFactor: device.formFactor,
      standalone: device.isStandalonePwa,
      hasCamera: device.hasCamera,
      hasWebNfc: device.hasWebNfc,
      networkOnline: typeof navigator === "undefined" || navigator.onLine !== false,
    });
    // device facts are stable for the life of the mount; re-fire only if analytics re-arms.
  }, [analytics, errorReporter, verdictKnown, userId, config.accountId]);
  useEffect(() => {
    if (blocked) {
      analytics.capture("web_access_blocked");
    }
  }, [analytics, blocked]);
  if (!verdictKnown) {
    return loading ? (
      <BootScreen tone="muted" headline="Checking access…" detail="Verifying your team's web access policy." />
    ) : (
      <WebAccessUnverified onRetry={() => window.location.reload()} onSignOut={() => void signOut()} />
    );
  }
  if (blocked) {
    return <WebAccessBlocked accountName={config.accountName} onSignOut={() => void signOut()} />;
  }
  const currentAccount = accounts.find((account) => account.accountId === config.accountId) ?? null;
  const officerName = deriveOfficerName({ user });
  const officerRole = formatAccountRoleLabel(currentAccount?.role ?? "member");
  return (
    <div className="shell">
      <header className="shell__top">
        <div>
          <p className="shell__eyebrow">SecOps Guard</p>
          {accounts.length > 1 ? (
            <label className="shell__workspace">
              <span className="shell__workspace-label">Workspace</span>
              <select
                aria-label="Workspace"
                className="shell__workspace-select"
                value={config.accountId}
                onChange={(event) => onSelectWorkspace(event.target.value)}
              >
                {accounts.map((account) => (
                  <option key={account.accountId} value={account.accountId}>
                    {account.accountName}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <h1 className="shell__account">{config.accountName}</h1>
          )}
        </div>
        <div className="shell__actions">
          {flags.fieldStatus ? (
            <button
              className="shell__statusbtn"
              type="button"
              aria-pressed={view === "status"}
              onClick={() => {
                const next = view === "status" ? "duty" : "status";
                if (next === "status") {
                  analytics.capture("field_status_opened");
                }
                setView(next);
              }}
            >
              Status
            </button>
          ) : null}
          <button className="shell__signout" type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>
      <nav className="shell__primary-nav" aria-label="Guard Web sections">
        <button
          aria-pressed={view === "duty"}
          type="button"
          onClick={() => setView("duty")}
        >
          Duty
        </button>
        <button
          aria-pressed={view === "inbox"}
          type="button"
          onClick={() => setView("inbox")}
        >
          Inbox
        </button>
      </nav>
      <main className="shell__body" role="main">
        {view === "status" && flags.fieldStatus ? (
          <FieldStatus
            appVersion={__APP_VERSION__}
            device={device}
            checkpointCount={routeCheckpoints.data.checkpoints?.length ?? 0}
            hasRouteAssignment={Boolean(routeCheckpoints.data.routeAssignmentId)}
            captureFlags={{
              qrCapture: flags.qrCapture,
              nfcCapture: flags.nfcCapture,
              manualCapture: flags.manualCapture,
            }}
            timeclockEnabled={flags.timeclock}
            notificationsEnabled={flags.notifications}
            onBack={() => setView("duty")}
          />
        ) : view === "inbox" ? (
          <FieldInbox
            key={`${config.accountId}:${userId}`}
            accountId={config.accountId}
            client={client}
            currentAuthorName={officerName}
            currentAuthorRole={officerRole}
            officerId={userId}
          />
        ) : (
          <>
            {flags.timeclock ? (
              <TimeclockPanel
                client={client as unknown as TimePunchRpcClient}
                accountId={config.accountId}
                deviceId={deviceId}
                routeAssignmentId={routeCheckpoints.data.routeAssignmentId}
                analytics={analytics}
              />
            ) : null}
            {flags.dutyHome ? (
              <DutyProgress checkpoints={routeCheckpoints.data.checkpoints ?? []} capturedIds={capturedIds} />
            ) : null}
            {flags.myShifts ? (
              <MyShiftsPanel
                client={client as unknown as MyShiftsQueryClient & ScheduleResponseRpcClient}
                accountId={config.accountId}
              />
            ) : null}
            {flags.myShifts ? (
              <OpenShiftsPanel
                key={config.accountId}
                client={client as unknown as OpenShiftsQueryClient & ClaimOpenShiftRpcClient}
                accountId={config.accountId}
              />
            ) : null}
            <p className="shell__hello">
              On duty: <strong className="shell__officer">{officerName}</strong>
              <span className="shell__device">{describeDevice(device)}</span>
            </p>
            <CaptureScreen
              client={client}
              accountId={config.accountId}
              device={device}
              deviceId={deviceId}
              checkpoints={routeCheckpoints.data.checkpoints}
              routeAssignmentId={routeCheckpoints.data.routeAssignmentId}
              featureFlags={{
                qrCapture: flags.qrCapture,
                nfcCapture: flags.nfcCapture,
                manualCapture: flags.manualCapture,
              }}
              analytics={analytics}
              onCaptured={(mark) =>
                setCapturedIds((previous) => markCaptured(previous, routeCheckpoints.data.checkpoints ?? [], mark))
              }
            />
          </>
        )}
        <BuildIdentity appVersion={__APP_VERSION__} config={config} />
      </main>
    </div>
  );
}

function BootScreen({
  tone,
  headline,
  detail,
  problems,
}: {
  tone: "muted" | "error";
  headline: string;
  detail: string;
  problems?: string[];
}) {
  const device = detectDeviceContext();
  return (
    <div className="boot">
      <main className="boot__card" role="main" aria-labelledby="boot-title">
        <BrandMark className="boot__mark" />
        <p className="boot__eyebrow">SecOps</p>
        <h1 id="boot-title" className="boot__title">
          Guard Web
        </h1>
        {tone === "error" ? (
          <p className="boot__error" role="alert">
            {headline}
          </p>
        ) : (
          <p className="boot__tagline">{headline}</p>
        )}
        <p className="boot__detail">{detail}</p>
        {problems !== undefined && problems.length > 0 ? (
          <ul className="boot__problems" aria-label="Configuration problems">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        ) : null}
        <p className="boot__device" aria-label="Detected device">
          {describeDevice(device)}
        </p>
        <p className="boot__version" aria-label="Application version" title={__APP_VERSION__}>
          v{(__APP_VERSION__.includes("+") ? __APP_VERSION__.slice(0, __APP_VERSION__.indexOf("+")) : __APP_VERSION__) || "26.08.0001"}
        </p>
      </main>
    </div>
  );
}
